import React, { useState, useEffect, useRef, useMemo } from "react";

import { RadioGroup } from "@cayuse/shared-react.components.form.radio-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import {
	isNonEmptyArray,
	isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";
import { v4 as uuidv4 } from "uuid";

import { RESEARCH_TEAM } from "globals/constants/formObjects";

import FlowableFinder from "../Common/FlowableFinder";
import usePersonSearch from "../FlowablePersonSearch/components/hooks/usePersonSearch";
import { initialAssociation } from "./constants";
import { useRoleFinder } from "./custom-hooks/useRoleFinder";
import { GuestComponent } from "./GuestComponent";
import { RTAssociations } from "./RTAssociations";
import styles from "./styles.css";
import { getPreselectedAssociation, getSelectConfig, validateIsRequired } from "./utils";

const { ASSOCIATION_TYPE } = RESEARCH_TEAM;

const getAssociationTypeOptions = dictionary => {
	return [
		{
			label: dictionary.internalAssociationLabel || "Internal Association",
			value: "internal",
		},
		{
			label: dictionary.externalAssociationLabel || "External Association",
			value: "external",
		},
	];
};

export const RTMember = props => {
	const {
		item,
		index,
		bem,
		config,
		Components,
		onChange,
		teamMembers,
		members,
		memberToAssociationLookupMap,
		selectedAssociationsDetails,
		getRoles,
		updateValue,
		restProps,
		isPresent,
		dictionary = {},
		effortFormat,
		displayConditions,
		effortTypes,
		associationType,
		hasPrincipalInvestigator,
		setTouched
	} = props;

	const ref = useRef(null);
	const [ mounted, setMounted ] = useState();
	const [ guestMode, setGuestMode ] = useState(false);
	const [ memberConfig, setMemberConfig ] = useState();
	const [ roleConfig, seRoleConfig ] = useState();
	const [ type, setType ] = useState();
	const [ personId, setPersonId ] = useState();
	const [ role, setRole ] = useState();
	const [ users, setUsers ] = useState([]);
	const [ memberSearchId, setMemberSearchId ] = useState(() => uuidv4());
	const [ preselectedOptions, setPreselectedOptions ] = useState({
		[ASSOCIATION_TYPE.INTERNAL]: [],
		[ASSOCIATION_TYPE.EXTERNAL]: [],
	});
	const [ associationsConfig, setAssociationsConfig ] = useState([]);
	const [ removeModal, setRemoveModal ] = useState({
		showModal: false,
		memberDisplayName: "",
	});

	const { search } = usePersonSearch();

	const showGuestButton = useMemo(() =>
		config.enabled && config.extraSettings.allowNewPerson && type === ASSOCIATION_TYPE.EXTERNAL,
	[ type, config.enabled, config.extraSettings.allowNewPerson ]);

	useEffect(() => {
		setType(associationType);
		setPersonId(item.member);
		setRole(item.role);
		setGuestMode(!!item.guest && associationType === ASSOCIATION_TYPE.EXTERNAL);
		setMounted(true);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ ]);

	useEffect(() => {
		const memberSelectConfig = getSelectConfig(
			"Name ",
			config.$path,
			index,
			"member",
			members,
			{
				isRequired: config.isRequired,
				enabled: config.enabled
			}
		);
		setMemberConfig(memberSelectConfig);

		const roleSelectConfig = getSelectConfig(
			"Role ",
			config.$path,
			index,
			"role",
			getRoles(item),
			{
				isRequired: displayConditions.isRequired,
				enabled: config.enabled,
				hasPrincipalInvestigator
			}
		);
		seRoleConfig(roleSelectConfig);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[ config.enabled, hasPrincipalInvestigator ]);

	useEffect(() => {
		if (!mounted) {
			return;
		}

		if(type !== associationType) {
			updateValue(onChange, value => {
				value[index] = {
					associationType: type,
					member: null,
					role: null,
					associations: type === ASSOCIATION_TYPE.INTERNAL ? [ initialAssociation ] : [],
					externalAssociations: type === ASSOCIATION_TYPE.EXTERNAL ? [ { association: null } ] : [],
				};
			});
		} else {
			if(personId !== item?.member) {
				setContent("member", personId, true);
			}

			if(role !== item?.role) {
				setContent("role", role);
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[ type, personId, role ]);

	useEffect(() => {
		if (isNonEmptyString(personId)) {
			const userOption = users.find(user => user.key === personId);
			if(userOption) {
				setRemoveModal(prevState => ({
					...prevState,
					memberDisplayName: ` ${userOption.text}`,
				}));
				setAssociationsConfig(userOption.person.associations);
				setPreselectedOptions(prevState => ({
					...prevState,
					[type]: [ userOption ],
				}));
			} else {
				getPreselectedAssociation(personId).then(user => {
					setRemoveModal(prevState => ({
						...prevState,
						memberDisplayName: ` ${user.name}`,
					}));
					setAssociationsConfig(user.person.associations);
					setPreselectedOptions(prevState => ({
						...prevState,
						[type]: [ user ],
					}));
				});
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ type, personId ]);

	const setContent = (field, newValue, shouldUpdateAssociations) => {
		let setValue = value => (value[index][field] = newValue);

		if (field === "member") {
			setValue = value =>
				(value[index] = {
					...value[index],
					member: newValue,
					associationType: type,
					associations: type === ASSOCIATION_TYPE.INTERNAL ? [ initialAssociation ] : [],
					guest: null,
					externalAssociations: type === ASSOCIATION_TYPE.EXTERNAL ? [
						{
							association: null,
						},
					] : [],
				});
		}
		if (field === "role") {
			setValue = value =>
				(value[index] = {
					...value[index],
					role: newValue,
				});
		}
		if (field.includes("guest")) {
			setValue = value => {
				if (!value[index].guest) {
					value[index].guest = {};
				}
				const fieldName = field.split(".")[1];
				value[index] = {
					...value[index],
					member: null,
					guest: { ...value[index].guest, [fieldName]: newValue },
				};

				return (value[index].guest[fieldName] = newValue);
			};
		}
		updateValue(onChange, setValue, shouldUpdateAssociations);
	};

	const deleteEntry = () => {
		updateValue(onChange, value => value.splice(index, 1));

		setRemoveModal({
			memberDisplayName: "",
			showModal: false,
		});
	};

	const updateAssociationType = newValue => {
		setType(newValue);
		setGuestMode(false);
		setPersonId(null);
		setRole(null);
		ref.current.resetAssociations();
	};

	const key = `${index}-`;

	const nameChanged = selectedOptions => {
		setPersonId(selectedOptions[0]?.person.personId);
		setMemberSearchId(uuidv4());
		ref.current.resetAssociations();
		if (!isNonEmptyArray(selectedOptions)) {
			setRemoveModal(prevState => ({
				...prevState,
				memberDisplayName: "",
			}));
			setPreselectedOptions(prevState => ({
				...prevState,
				[type]: [],
			}));
		}
	};

	const searchUsers = searchText => {
		const key = type === ASSOCIATION_TYPE.EXTERNAL ? "externalAffiliations" : "affiliations";
		const options = {
			keyParamName: "personId",
			searchParameters: [ { key, operator: "^" } ],
		};

		return search(searchText, options).then(item => {
			setUsers([ ...item.options.filter(option => option.person) ]);

			return item;
		});
	};

	const addGuest = () => {
		setGuestMode(true);
		setPersonId(null);
		updateValue(onChange, value => {
			value[index] = {
				...value[index],
				member: null,
				role: null,
				guest: {
					firstName: null,
					middleName: null,
					lastName: null,
				},
				externalAssociations: [ { association: null } ],
			};
		});
		setRole(null);
		ref.current.resetAssociations();
	};

	const backToPersonSearch = () => {
		setGuestMode(false);
		updateValue(onChange, value => {
			value[index] = {
				...value[index],
				role: null,
				guestMode: false,
				externalAssociations: [ { association: null } ],
				guest: null,
			};
		});
		setRole(null);
		ref.current.resetAssociations();
	};

	const roleFinderProps = useRoleFinder({ config: { ...roleConfig, value: role }, setRole });

	const handleFocus = () => {
		setTouched(true);
	};

	if(!mounted) {
		return null;
	}

	return (
		<div
			key={`row-${index}`}
			className={`${styles.member} ${styles.lightGreyBackground}`}
			onFocus={handleFocus}
		>
			{displayConditions.externalResearchers && (
				<div className={styles.associationTypeToggleContainer}>
					<RadioGroup
						label={dictionary.associationTypeToggleLabel || "Association Type"}
						className={styles.radioGroup}
						value={type}
						disabled={!config.enabled}
						onChange={value => updateAssociationType(value)}
						options={getAssociationTypeOptions(dictionary)}
					/>
				</div>
			)}
			<div
				className={`${bem("row")} ${
                    styles.memberInfo
                } flw__panel--required`}
			>
				{!guestMode && (
					<div className={bem(`col--6`)}>
						<FlowableFinder
							search={searchUsers}
							searchChange={nameChanged}
							preselectedOptions={
								preselectedOptions[type]
							}
							{...restProps}
							config={{
								...memberConfig,
								value: personId,
								$errors: validateIsRequired(personId, memberConfig?.isRequired)
							}}
							key={memberSearchId}
							className="memberFinderWrapper"
						/>
					</div>
				)}
				{guestMode && <GuestComponent
					path={config.$path}
					index={index}
					guest={item.guest}
					enabled={config.enabled}
					bem={bem}
					setContent={setContent}
					Components={Components}
					required={config.isRequired}
					restProps={restProps}
				              />
				}

				<div className={bem(`col--6`)}>
					<FlowableFinder
						{...roleFinderProps}
						{...restProps}
						config={{
							...roleConfig,
							value: role,
							$errors: validateIsRequired(role, roleConfig?.isRequired)
						}}
						className="roleFinderWrapper"
					/>
				</div>
			</div>
			{showGuestButton && !guestMode && (
				<div className={bem(`col--12`)}>
					<IconButton
						icon="plus"
						onClick={addGuest}
						labelPosition="left"
						secondary
						className={styles.guestButton}
					>
						{dictionary.addGuest ?? "Add a new external team member"}
					</IconButton>
				</div>
			)}
			{showGuestButton && guestMode && (
				<div className={bem(`col--12`)}>
					<IconButton
						icon="arrow-left"
						onClick={backToPersonSearch}
						labelPosition="left"
						secondary
						className={styles.guestButton}
					>
						{dictionary.backToPersonSearch ?? "Back to person search"}
					</IconButton>
				</div>
			)}
			<div className={styles.associationsWrapper}>
				<RTAssociations
					ref={ref}
					config={config}
					onChange={onChange}
					teamMembers={teamMembers}
					index={index}
					rowKey={key}
					Components={Components}
					associationsConfig={associationsConfig}
					memberToAssociationLookupMap={memberToAssociationLookupMap}
					selectedAssociationsDetails={selectedAssociationsDetails}
					updateValue={updateValue}
					isPresent={isPresent}
					restProps={restProps}
					dictionary={dictionary}
					effortFormat={effortFormat}
					effortTypes={effortTypes}
					displayConditions={displayConditions}
					associationType={type}
					guestMode={guestMode}
				/>
			</div>
			{index !== 0 && (
				<>
					<div className={styles.deleteMember}>
						<IconButton
							icon="trash"
							disabled={!config.enabled}
							onClick={() =>
								setRemoveModal(prevState => ({
									...prevState,
									showModal: true,
								}))
							}
							labelPosition="left"
							secondary
							className={styles.lightBackground}
						>
							{dictionary.removeTeamMemberButton ||
                                "Remove Team Member"}
						</IconButton>
					</div>
					{removeModal.showModal && (
						<AlertDialog
							title={
								dictionary.modal.removeTeamMemberTitle ??
                                "Remove team member?"
							}
							description={
								<>
									{dictionary.modal
										.removeTeamMemberDescription ||
                                        "Remove team member"}
									<b>{removeModal.memberDisplayName}</b>?
								</>
							}
							isOpen={removeModal.showModal}
							onConfirm={deleteEntry}
							confirmText={
								dictionary.removeTeamMemberButton ||
                                "Remove Team Member"
							}
							onCancel={() =>
								setRemoveModal(prevState => ({
									...prevState,
									showModal: false,
								}))
							}
						/>
					)}
				</>
			)}
		</div>
	);
};
